/* eslint-disable arrow-parens */
/* eslint-disable prefer-destructuring */
import axios from 'axios';
import { AxiosRequestConfig } from 'axios';

function resolveMainApiBaseUrl(): string {
  const raw = process.env.REACT_APP_API_URL?.trim();
  if (raw && raw !== 'undefined' && raw !== 'null') {
    return `${raw.replace(/\/+$/, '')}/api`;
  }
  if (process.env.NODE_ENV === 'development') {
    return '/api';
  }
  return '/api';
}

const BASE_API_URL = resolveMainApiBaseUrl();

const instance = axios.create({
  baseURL: BASE_API_URL,
});
const parseErrors = (error: any) => error;

const setHeader = (headerName: string, headerValue: string) => {
  instance.defaults.headers.common[headerName] = headerValue;
};

const removeHeader = (headerName: string) => {
  delete instance.defaults.headers.common[headerName];
};

const getHeader = (headerName: string) =>
  instance.defaults.headers.common[headerName];

const post = async <T, D>(
  url: string,
  data: T,
  config?: AxiosRequestConfig<T>,
) => {
  try {
    const response = await instance.post<D>(url, data, config || {});
    return response;
  } catch (error) {
    throw parseErrors(error);
  }
};

const put = async <T, D>(url: string, data: T) => {
  try {
    const response = await instance.put<D>(url, data);
    return response;
  } catch (error) {
    throw parseErrors(error);
  }
};

const patch = async <T, D>(url: string, data: T) => {
  try {
    const response = await instance.patch<D>(url, data);
    return response;
  } catch (error) {
    throw parseErrors(error);
  }
};

/** Coalesce identical in-flight GETs so sibling mounts share one network call. */
const inflightGets = new Map<string, Promise<unknown>>();

const getInflightKey = (
  url: string,
  config?: AxiosRequestConfig<unknown>,
): string =>
  JSON.stringify({
    url,
    params: config?.params ?? null,
    headers: config?.headers ?? null,
  });

const get = async <D>(url: string, config?: AxiosRequestConfig<any>) => {
  const key = getInflightKey(url, config);
  const existing = inflightGets.get(key);
  if (existing) {
    return existing as ReturnType<typeof instance.get<D>>;
  }

  const request = instance
    .get<D>(url, config || {})
    .catch((error: unknown) => {
      throw parseErrors(error);
    })
    .finally(() => {
      inflightGets.delete(key);
    });

  inflightGets.set(key, request);
  return request;
};

const del = async (url: string) => {
  try {
    const response = await instance.delete(url);
    return response;
  } catch (error) {
    throw parseErrors(error);
  }
};

const delWithBody = async <T>(url: string, body: T) => {
  try {
    const response = await instance.delete(url, { data: body });
    return response;
  } catch (error) {
    throw parseErrors(error);
  }
};

export {
  instance,
  post,
  patch,
  put,
  get,
  del,
  delWithBody,
  setHeader,
  getHeader,
  removeHeader,
};
